import { useEffect, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon } from 'lucide-react';
import type { Notification } from '@kennisbank/shared';
import { apiPost } from '../api/client.js';

/**
 * Bel in de header met het meldingenpaneel.
 *
 * De data komt van buiten (Layout haalt ze op in de bestaande poll van 60s);
 * dit component bezit alleen het openen/sluiten en het markeren-als-gelezen.
 * Zo blijft er één ophaalmoment voor de hele app.
 *
 * Zichtbaar voor élke rol, ook zorggroep-gebruikers. Dat wijkt bewust af van
 * de sidebar-badges achter `isInternal`: die zeggen "daar ligt werk", de bel
 * zegt "dit is er gebeurd" en externen krijgen terugkoppeling op hun eigen
 * inzendingen.
 */

interface NotificatieBelProps {
  notifications: Notification[];
  unread: number;
  /** Haalt de lijst opnieuw op, zodat de teller niet een minuut achterloopt. */
  onRefresh: () => void;
}

const MINUUT = 60_000;
const UUR = 60 * MINUUT;
const DAG = 24 * UUR;
const WEEK = 7 * DAG;

const RELATIEF = new Intl.RelativeTimeFormat('nl-NL', { numeric: 'auto' });
const DATUM = new Intl.DateTimeFormat('nl-NL', {
  day: 'numeric',
  month: 'short',
  timeZone: 'Europe/Amsterdam',
});

/** "2 uur geleden", "gisteren", "3 weken geleden", en daarna een kale datum. */
function tijdGeleden(iso: string, nu: number): string {
  const verstreken = nu - new Date(iso).getTime();
  if (!Number.isFinite(verstreken)) return '';
  if (verstreken < MINUUT) return 'zojuist';
  if (verstreken < UUR) return RELATIEF.format(-Math.floor(verstreken / MINUUT), 'minute');
  if (verstreken < DAG) return RELATIEF.format(-Math.floor(verstreken / UUR), 'hour');
  if (verstreken < WEEK) return RELATIEF.format(-Math.floor(verstreken / DAG), 'day');
  if (verstreken < 4 * WEEK) return RELATIEF.format(-Math.floor(verstreken / WEEK), 'week');
  return DATUM.format(new Date(iso));
}

export function NotificatieBel({
  notifications,
  unread,
  onRefresh,
}: NotificatieBelProps): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const belRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      belRef.current?.focus();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Het paneel gaat pas open na een klik, dus de tijden worden per opening
  // berekend; een tikkende klok voegt niets toe aan "2 uur geleden".
  const nu = Date.now();

  const markeerGelezen = (id: string): void => {
    void apiPost(`/api/v1/notifications/${id}/read`)
      .catch(() => undefined)
      .finally(() => onRefresh());
  };

  const openMelding = (melding: Notification): void => {
    setOpen(false);
    if (!melding.readAt) markeerGelezen(melding.id);
    navigate(melding.link);
  };

  const allesGelezen = (): void => {
    void apiPost('/api/v1/notifications/read-all')
      .catch(() => undefined)
      .finally(() => onRefresh());
  };

  const belLabel =
    unread > 0 ? `Meldingen, ${unread} ongelezen` : 'Meldingen, geen ongelezen meldingen';

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        ref={belRef}
        onClick={() => setOpen((v) => !v)}
        aria-label={belLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="relative rounded-lg p-2 text-ink-900/70 transition-colors hover:bg-brand-50 hover:text-ink-900"
      >
        <BellIcon size={20} strokeWidth={2} />
        {unread > 0 && (
          // Vaste hoogte plus dezelfde min-breedte maakt één cijfer een echte
          // cirkel; met verticale padding werd het 18 bij 14 en dus een ovaal.
          // Bij "99+" groeit hij mee tot een pil, en dat hoort ook zo.
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand-500 px-1 text-[10px] font-semibold leading-none text-white"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        // Op mobiel staat de avatar rechts van de bel, dus rechts uitlijnen op
        // de bel schoof het paneel links buiten beeld. Daar hangt het daarom
        // vast onder de header (h-16) over de volle breedte.
        <div
          role="dialog"
          aria-label="Meldingen"
          className="fixed inset-x-3 top-[4.5rem] z-40 overflow-hidden sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[22rem] rounded-lg border border-brand-100 bg-white shadow-lg"
        >
          <div className="flex items-center justify-between gap-2 border-b border-brand-50 px-4 py-2.5">
            <span className="text-[13px] font-semibold text-ink-800">Meldingen</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={allesGelezen}
                className="rounded-md px-2 py-1 text-[12px] font-medium text-brand-700 transition-colors hover:bg-brand-50"
              >
                Alles gelezen
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
              <BellIcon size={32} strokeWidth={1.5} className="text-ink-900/25" />
              <p className="text-[13px] text-ink-900/55">Je hebt geen meldingen.</p>
            </div>
          ) : (
            <ul className="max-h-[24rem] overflow-y-auto">
              {notifications.map((melding) => {
                const ongelezen = !melding.readAt;
                return (
                  <li key={melding.id} className="border-b border-brand-50 last:border-b-0">
                    <button
                      type="button"
                      onClick={() => openMelding(melding)}
                      className={`flex w-full items-start gap-2.5 px-4 py-3 text-left transition-colors hover:bg-brand-50/60 ${
                        ongelezen ? 'bg-brand-50/40' : ''
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                          ongelezen ? 'bg-brand-500' : 'bg-transparent'
                        }`}
                      />
                      <span className="min-w-0 flex-1">
                        <span
                          className={`block text-[13px] leading-snug ${
                            ongelezen ? 'font-semibold text-ink-900' : 'text-ink-900/70'
                          }`}
                        >
                          {melding.title}
                        </span>
                        <span className="mt-1 flex items-center gap-2 text-[11px] text-ink-900/50">
                          <span>{tijdGeleden(melding.updatedAt, nu)}</span>
                          {!!melding.count && melding.count > 1 && (
                            <span className="rounded-sm bg-brand-50 px-1.5 py-0.5 font-medium text-brand-700">
                              {melding.count} keer
                            </span>
                          )}
                          {ongelezen && <span className="sr-only">Ongelezen</span>}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
